import cn from 'classnames';
import PropTypes from 'prop-types';

import { ADDRESS_FIELDS } from 'constants/formFields';
import { INVALID_ADDRESS } from 'constants/errorMessages';

import ErrorList from 'components/checkout/caap/ErrorList';
import { Row, RowSpan } from 'components/checkout/caap/Layout';
import CountryList from 'components/common/CountryList';
import { TextField } from 'components/Mdl';
import { getFieldFocusEvents } from 'helpers/CheckoutUtils';
import css from 'styles/components/checkout/caap/address/addressForm.scss';

const hasError = (key, errors) => errors[key] && errors[key].length;

const fieldFocusEvents = getFieldFocusEvents(css.hasFormElementWithFocus);

const poHelperText = <div className={css.poHelperBox}>
  Trying to enter a PO Box? Please enter it in Address Line 1 and exclude the street name, e.g. <strong>PO BOX 89</strong>
</div>;

const AddressForm = ({ address, hasInvalidAddress, isBilling, isEditAddressStep, onCountryDropdownChange, onFieldChange, showPoBoxHelperText }, { testId }) => {

  const errors = address.errors || {};

  let errorList = [];

  Object.keys(errors).forEach(key => {
    // do not include the error if it was related to invalid address
    if (errors[key] && errors[key].length && errors[key][0] !== INVALID_ADDRESS) {
      errorList = [...errorList, ...errors[key]];
    }
  });

  if (hasInvalidAddress) {
    errorList = ['It seems we are unable to determine if the address is valid or not.  If you are sure of your entry please try submitting again.', ...errorList ];
  }

  const wrapperStyles = {
    [css.formInputWrapper]: true
  };

  const fullNameWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.first]: true,
      [css.error]: hasError(ADDRESS_FIELDS.FULL_NAME.fieldName, errors)
    }
  );

  const addressLine1WrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.error]: hasError(ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName, errors)
    }
  );

  const addressLine2WrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.error]: hasError(ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName, errors)
    }
  );

  const cityWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.error]: hasError(ADDRESS_FIELDS.CITY.fieldName, errors)
    }
  );

  const stateOrRegionWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.error]: hasError(ADDRESS_FIELDS.STATE_OR_REGION.fieldName, errors)
    }
  );

  const postalCodeWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.error]: hasError(ADDRESS_FIELDS.POSTAL_CODE.fieldName, errors)
    }
  );

  const phoneNumberWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.last]: true,
      [css.error]: hasError(ADDRESS_FIELDS.PHONE_NUMBER.fieldName, errors)
    }
  );

  const countryCodeWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.countryContainer]: true,
      [css.solo]: true,
      [css.error]: hasError(ADDRESS_FIELDS.COUNTRY_CODE.fieldName, errors)
    }
  );

  const errorHeadingMobile = hasInvalidAddress ? 'Please correct the fields marked below:' : 'Important Message';
  const errorHeadingDesktop = 'Important Message';
  const errorListVerbiage = ['Please correct the fields marked below.'];

  return (
    <Row extraClass={css.form} isVerticalForm={true}>
      <div className={css.showOnlySmallScreen}>
        <ErrorList errorList={errorList} errorHeading={errorHeadingMobile} />
      </div>
      <div className={css.hideSmallScreen}>
        <ErrorList errorList={errorList.length && errorListVerbiage} errorHeading={errorHeadingDesktop} />
      </div>
      {
        showPoBoxHelperText && <div className={css.showOnlySmallScreen}>{poHelperText}</div>
      }
      <Row>
        <RowSpan mobileSpan="0" desktopSpan="4">
          <div className={css.formLabelContainer}>
            <label htmlFor={ADDRESS_FIELDS.FULL_NAME.fieldName}>Full name:</label>
          </div>
        </RowSpan>
        <RowSpan mobileSpan="12" desktopSpan="7">
          <div { ...fieldFocusEvents } className={fullNameWrapperStyles}>
            <TextField
              autoComplete="name"
              onChange={onFieldChange}
              value={address[ADDRESS_FIELDS.FULL_NAME.fieldName]}
              maxLength={ADDRESS_FIELDS.FULL_NAME.maxLength}
              name={ADDRESS_FIELDS.FULL_NAME.fieldName}
              id={ADDRESS_FIELDS.FULL_NAME.fieldName}
              placeholder="Full Name"
              required
              data-test-id={testId('addressName')} />
          </div>
        </RowSpan>
      </Row>
      {
        showPoBoxHelperText && <Row>
          <RowSpan mobileSpan="0" desktopSpan="4">&nbsp;</RowSpan>
          <RowSpan mobileSpan="0" desktopSpan="7">
            { poHelperText }
          </RowSpan>
        </Row>
      }
      <Row>
        <RowSpan mobileSpan="0" desktopSpan="4">
          <div className={css.formLabelContainer}>
            <label htmlFor={ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName}>Address line 1:</label>
          </div>
        </RowSpan>
        <RowSpan mobileSpan="12" desktopSpan="7">
          <div { ...fieldFocusEvents } className={addressLine1WrapperStyles}>
            <TextField
              autoComplete="address-line1"
              onChange={onFieldChange}
              value={address[ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName]}
              name={ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName}
              id={ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName}
              maxLength={ADDRESS_FIELDS.ADDRESS_LINE_1.maxLength}
              placeholder="Street address, PO BOX, company name, c/o"
              data-test-id={testId('addressLine1')} />
          </div>
        </RowSpan>
      </Row>

      <Row>
        <RowSpan mobileSpan="0" desktopSpan="4">
          <div className={css.formLabelContainer}>
            <label htmlFor={ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName}>Address line 2:</label>
          </div>
        </RowSpan>
        <RowSpan mobileSpan="12" desktopSpan="7">
          <div { ...fieldFocusEvents } className={addressLine2WrapperStyles}>
            <TextField
              autoComplete="address-line2"
              onChange={onFieldChange}
              value={address[ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName]}
              name={ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName}
              id={ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName}
              maxLength={ADDRESS_FIELDS.ADDRESS_LINE_2.maxLength}
              placeholder="Apartment, suite, unit, building, floor, etc."
              data-test-id={testId('addressLine2')} />
          </div>
        </RowSpan>
      </Row>

      <Row>
        <RowSpan mobileSpan="0" desktopSpan="4">
          <div className={css.formLabelContainer}>
            <label htmlFor={ADDRESS_FIELDS.CITY.fieldName}>City:</label>
          </div>
        </RowSpan>
        <RowSpan mobileSpan="12" desktopSpan="7">
          <div { ...fieldFocusEvents } className={cityWrapperStyles}>
            <TextField
              autoComplete="address-level2"
              onChange={onFieldChange}
              value={address[ADDRESS_FIELDS.CITY.fieldName]}
              name={ADDRESS_FIELDS.CITY.fieldName}
              id={ADDRESS_FIELDS.CITY.fieldName}
              maxLength={ADDRESS_FIELDS.CITY.maxLength}
              placeholder="City"
              data-test-id={testId('addressCity')} />
          </div>
        </RowSpan>
      </Row>

      <Row>
        <RowSpan mobileSpan="0" desktopSpan="4">
          <div className={css.formLabelContainer}>
            <label htmlFor={ADDRESS_FIELDS.STATE_OR_REGION.fieldName}>State/Province/Region:</label>
          </div>
        </RowSpan>
        <RowSpan mobileSpan="12" desktopSpan="7">
          <div { ...fieldFocusEvents } className={stateOrRegionWrapperStyles}>
            <TextField
              autoComplete="address-level1"
              onChange={onFieldChange}
              value={address[ADDRESS_FIELDS.STATE_OR_REGION.fieldName]}
              name={ADDRESS_FIELDS.STATE_OR_REGION.fieldName}
              id={ADDRESS_FIELDS.STATE_OR_REGION.fieldName}
              maxLength={ADDRESS_FIELDS.STATE_OR_REGION.maxLength}
              placeholder="State/Province/Region"
              data-test-id={testId('addressState')} />
          </div>
        </RowSpan>
      </Row>

      <Row>
        <RowSpan mobileSpan="0" desktopSpan="4">
          <div className={css.formLabelContainer}>
            <label htmlFor={ADDRESS_FIELDS.POSTAL_CODE.fieldName}>ZIP:</label>
          </div>
        </RowSpan>
        <RowSpan mobileSpan="12" desktopSpan="7">
          <div { ...fieldFocusEvents } className={postalCodeWrapperStyles}>
            <TextField
              autoComplete="postal-code"
              onChange={onFieldChange}
              value={address[ADDRESS_FIELDS.POSTAL_CODE.fieldName]}
              name={ADDRESS_FIELDS.POSTAL_CODE.fieldName}
              id={ADDRESS_FIELDS.POSTAL_CODE.fieldName}
              maxLength={ADDRESS_FIELDS.POSTAL_CODE.maxLength}
              placeholder="ZIP"
              data-test-id={testId('addressPostalCode')} />
          </div>
        </RowSpan>
      </Row>

      <Row>
        <RowSpan mobileSpan="0" desktopSpan="4">
          <div className={css.formLabelContainer}>
            <label htmlFor={ADDRESS_FIELDS.PHONE_NUMBER.fieldName}>Phone number:</label>
          </div>
        </RowSpan>
        <RowSpan mobileSpan="12" desktopSpan="7">
          <div { ...fieldFocusEvents } className={phoneNumberWrapperStyles}>
            <TextField
              autoComplete="tel-national"
              onChange={onFieldChange}
              value={address[ADDRESS_FIELDS.PHONE_NUMBER.fieldName]}
              name={ADDRESS_FIELDS.PHONE_NUMBER.fieldName}
              id={ADDRESS_FIELDS.PHONE_NUMBER.fieldName}
              maxLength={ADDRESS_FIELDS.PHONE_NUMBER.maxLength}
              placeholder="Phone Number"
              data-test-id={testId('addressPhoneNumber')} />
          </div>
        </RowSpan>
      </Row>

      <Row>
        <RowSpan mobileSpan="0" desktopSpan="4">
          <div className={css.formLabelContainer}>
            <label htmlFor={ADDRESS_FIELDS.COUNTRY_CODE.fieldName}>Country:</label>
          </div>
        </RowSpan>
        <RowSpan mobileSpan="12" desktopSpan="7">
          <div { ...fieldFocusEvents } className={countryCodeWrapperStyles} data-test-id={testId('addressCountry')}>
            <CountryList
              isBilling={isBilling || isEditAddressStep}
              onChange={onCountryDropdownChange}
              defaultValue={address[ADDRESS_FIELDS.COUNTRY_CODE.fieldName]}
              maxLength={ADDRESS_FIELDS.COUNTRY_CODE.maxLength}
              id={ADDRESS_FIELDS.COUNTRY_CODE.fieldName}
              name={ADDRESS_FIELDS.COUNTRY_CODE.fieldName} />
          </div>
        </RowSpan>
      </Row>
    </Row>
  );
};

AddressForm.contextTypes = {
  testId: PropTypes.func
};

export default AddressForm;
